import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  signal,
} from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import {
  CBox_GetInvoiceDataResponse,
  CBox_GetInvoiceDocumentDataResponse,
  CBox_GetInvoicesListDataParams,
  CBox_InvoiceListItem,
} from "@server/services/cbox/public/api/v1/resources/financial/invoices/types";
import { CBox_GetLockerBaseData } from "@server/services/cbox/public/api/v1/resources/locker/types";
import { PaginatedTableWithFilters } from "app/shared/paginated-table-with-filters";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { DropdownModule } from "primeng/dropdown";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { MenuModule } from "primeng/menu";
import { ProgressBarModule } from "primeng/progressbar";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { lockerCleanAddress } from "src/helpers/functions";
import { ProfileService } from "src/services/profile/profile.service";
import { TagModule } from "primeng/tag";
import { MenuItem } from "primeng/api";
import { CBox_PublicSuccessResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { fileDownloadHelper } from "src/helpers/file-download";
import { SkeletonModule } from "primeng/skeleton";
import { CBox_InvoicingDocumentComputedMetadataKey } from "@server/services/cbox/common/invoicing/metadata/metadata_keys";
import { DividerModule } from "primeng/divider";
import { CopyDirective } from "src/directives/copy.directive";

type CBox_InvoiceEInvoiceStatus = NonNullable<CBox_InvoiceListItem["eInvoicing"]>["status"];

interface BoxTotals {
  count: number;
  discountCount: number;
  amount: number;
  discountAmount: number;
  finalAmount: number;
}

@Component({
  selector: "app-cbox-profile-finance-invoices-list",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    MenuModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    CalendarModule,
    DropdownModule,
    ProgressBarModule,
    TooltipModule,
    TagModule,
    MenuModule,
    SkeletonModule,
    DividerModule,

    // Directives
    CopyDirective
  ],
  templateUrl: "./cbox-profile-finance-invoices-list.component.html",
  styleUrls: ["./cbox-profile-finance-invoices-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CBoxProfileFinanceInvoicesListComponent extends PaginatedTableWithFilters<
  CBox_InvoiceListItem,
  CBox_GetInvoicesListDataParams
> {
  private fb = inject(FormBuilder);
  private profileService = inject(ProfileService);

  filtersForm: FormGroup = this.fb.group({
    page: [1],
    pageSize: [100],
    interval: [[this.oneMonthAgo, this.today]],
    identifier: [""],
  });

  sessionFiltersKey = "finance/invoices/filters";
  sessionFiltersToSave = ["page", "pageSize"];
  localStorageFiltersKey = "finance/invoices/filters";
  localStorageFiltersToSave: string[] = [];

  defaultFilters = signal({
    page: 1,
    pageSize: 100,
    interval: [this.oneMonthAgo, this.today],
    identifier: "",
  });
  dataFetchUrl = "financial/invoices";
  expandedRows = signal<Record<string, boolean>>({});

  statusesOptions = signal([
    { label: "In asteptare", value: "PENDING_APPROVAL" },
    { label: "Acceptate", value: "APPROVED" },
    { label: "Refuzate", value: "REJECTED" },
  ]);

  statusesTranslationsList = signal<Record<CBox_InvoiceEInvoiceStatus, string>>({
    QUEUED: "În așteptare",
    FAILURE: "Eroare",
    PROCESSED: "Procesată",
    SUCCESS: "Procesată cu succes",
    UPLOADED: "Încărcată în e-factura",
  });

  invoicesMenuItems = signal<Record<string, MenuItem[]>>({});
  downloading = signal<Record<string, boolean>>({});

  invoicesData = signal<
    Record<CBox_InvoiceListItem["identifier"], CBox_GetInvoiceDataResponse>
  >({});
  invoiceMetadataKeys = CBox_InvoicingDocumentComputedMetadataKey;

  detailsToggled = signal<Record<string, boolean>>({});

  constructor(inj: Injector) {
    super(inj);
  }

  async init(): Promise<void> {
    this.profileService.setTitle("Listă facturi");
  }

  expandRow(invoice: CBox_InvoiceListItem) {
    this.expandedRows.update((r) => ({ ...r, [invoice.identifier]: true }));
    this.fetchInvoiceData(invoice);
  }

  collapseRow(invoice: CBox_InvoiceListItem) {
    this.expandedRows.update((r) => {
      delete r[invoice.identifier];
      return { ...r };
    });
  }

  fetchInvoiceData(invoice: CBox_InvoiceListItem): void {
    this.api
      .get<CBox_PublicSuccessResponse<CBox_GetInvoiceDataResponse>>(
        `backend/financial/invoices/${invoice.identifier}`
      )
      .subscribe({
        next: (response) => {
          console.log(response, "PRE INVOICE DATA RESPONSE");
          this.invoicesData.update((r) => ({
            ...r,
            [invoice.identifier]: response.data,
          }));
        },
        error: () => {
          console.log("COLLAPSING ROW");
          this.collapseRow(invoice);
          this.toastService.showErrorToast(
            "Eroare",
            "A apărut o eroare la preluarea datelor despre factură!"
          );
        },
      });
  }

  formatAddress(a: CBox_GetLockerBaseData["addressParts"]): string {
    return lockerCleanAddress(a);
  }

  isPastChangeWindow(end?: string): boolean {
    return !!end && Date.now() > new Date(end).getTime();
  }

  getInvoiceMenuItems(invoice: CBox_InvoiceListItem): MenuItem[] {
    const documents = invoice.documents;
    const systemPdf = documents.find((d) => d.format === "PDF" && d.source === "GENERATED_BY_SYSTEM");
    const einvoicingPdf = documents.find((d) => d.format === "PDF" && d.source === "GENERATED_BY_EINVOCING");
    const derivedPdf = documents.find((d) => d.format === "PDF" && d.source === "DERIVED_FROM_EINVOICING");

    const systemZip = documents.find((d) => d.format === "ZIP" && d.source === "GENERATED_BY_SYSTEM");
    const einvoicingZip = documents.find((d) => d.format === "ZIP" && d.source === "GENERATED_BY_EINVOCING");
    const derivedZip = documents.find((d) => d.format === "ZIP" && d.source === "DERIVED_FROM_EINVOICING");

    const systemXml = documents.find((d) => d.format === "XML" && d.source === "GENERATED_BY_SYSTEM");
    const einvoicingXml = documents.find((d) => d.format === "XML" && d.source === "GENERATED_BY_EINVOCING");
    const derivedXml = documents.find((d) => d.format === "XML" && d.source === "DERIVED_FROM_EINVOICING");

    const systemXlsx = documents.find((d) => d.format === "XLSX" && d.source === "GENERATED_BY_SYSTEM");
    const einvoicingXlsx = documents.find((d) => d.format === "XLSX" && d.source === "GENERATED_BY_EINVOCING");
    const derivedXlsx = documents.find((d) => d.format === "XLSX" && d.source === "DERIVED_FROM_EINVOICING");

    return [
      {
        label: "PDF",
        icon: "pi pi-file-pdf",
        visible: !!systemPdf,
        command: () => {
          this.downloadDocument(invoice, systemPdf!);
        },
      },
      {
        label: "PDF (generat de e-factura)",
        icon: "pi pi-file-pdf",
        visible: !!einvoicingPdf,
        command: () => {
          this.downloadDocument(invoice, einvoicingPdf!);
        },
      },
      {
        label: "PDF (generat din fișier e-factura)",
        icon: "pi pi-file-pdf",
        visible: !!derivedPdf,
        command: () => {
          this.downloadDocument(invoice, derivedPdf!);
        },
      },
      {
        label: "Arhivă",
        icon: "pi pi-file-plus",
        visible: !!systemZip,
        command: () => {
          this.downloadDocument(invoice, systemZip!);
        },
      },
      {
        label: "Arhivă (generat de e-factura)",
        icon: "pi pi-file-plus",
        visible: !!einvoicingZip,
        command: () => {
          this.downloadDocument(invoice, einvoicingZip!);
        },
      },
      {
        label: "Arhivă (generat din fișier e-factura)",
        icon: "pi pi-file-plus",
        visible: !!derivedZip,
        command: () => {
          this.downloadDocument(invoice, derivedZip!);
        },
      },
      {
        label: "XML",
        icon: "pi pi-file",
        visible: !!systemXml,
        command: () => {
          this.downloadDocument(invoice, systemXml!);
        },
      },
      {
        label: "XML (generat de e-factura)",
        icon: "pi pi-file",
        visible: !!einvoicingXml,
        command: () => {
          this.downloadDocument(invoice, einvoicingXml!);
        },
      },
      {
        label: "XML (generat din fișier e-factura)",
        icon: "pi pi-file",
        visible: !!derivedXml,
        command: () => {
          this.downloadDocument(invoice, derivedXml!);
        },
      },
      {
        label: "Excel",
        icon: "pi pi-file-excel",
        visible: !!systemXlsx,
        command: () => {
          this.downloadDocument(invoice, systemXlsx!);
        },
      },
      {
        label: "Excel (generat de e-factura)",
        icon: "pi pi-file-excel",
        visible: !!einvoicingXlsx,
        command: () => {
          this.downloadDocument(invoice, einvoicingXlsx!);
        },
      },
      {
        label: "Excel (generat din fișier e-factura)",
        icon: "pi pi-file-excel",
        visible: !!derivedXlsx,
        command: () => {
          this.downloadDocument(invoice, derivedXlsx!);
        },
      },
    ];
  }

  hasMenuItems(invoice: CBox_InvoiceListItem): boolean {
    return this.getInvoiceMenuItems(invoice).some(item => item.visible);
  }

  menuClosed(invoice: CBox_InvoiceListItem): void {
    this.invoicesMenuItems.update((rows) => {
      delete rows[invoice.identifier];
      return rows;
    });
  }

  downloadDocument(
    invoice: CBox_InvoiceListItem,
    document: CBox_InvoiceListItem["documents"][number]
  ) {
    try {
      this.setAsDownloading(invoice);
      this.api
        .get<CBox_PublicSuccessResponse<CBox_GetInvoiceDocumentDataResponse>>(
          `backend/financial/invoices/document/${document?.identifier}`
        )
        .subscribe(
          (response) => {
            if (!response.data.data) {
              this.setAsNotDownloading(invoice);
              this.toastService.showErrorToast(
                "Eroare",
                "Eroare la descărcarea documentului!"
              );
              return;
            }
            fileDownloadHelper(
              { data: (response.data as any).data.data },
              document.format.toLowerCase(),
              response.data.fileName
            );
            this.setAsNotDownloading(invoice);
            this.toastService.showSuccessToast(
              "Confirmare",
              "Fișierul a fost descărcat cu succes!"
            );
          }, () => {
            this.setAsNotDownloading(invoice);
            this.toastService.showErrorToast(
              "Eroare",
              "A apărut o eroare la descărcarea fișierului!"
            );
          }
        );
    } catch (e) {
      this.setAsNotDownloading(invoice);
    }
  }

  getTotalWithoutVAT(preInvoiceData: CBox_GetInvoiceDataResponse): number {
    const items = preInvoiceData.items;
    return items.reduce((total, item) => total + (item.unitPrice * item.quantity), 0);
  }

  getTotalVAT(preInvoiceData: CBox_GetInvoiceDataResponse): number {
    const items = preInvoiceData.items;
    return items.reduce((total, item) => total + item.taxAmount, 0);
  }

  getTotalToPay(preInvoiceData: CBox_GetInvoiceDataResponse): number {
    const items = preInvoiceData.items;
    return items.reduce((total, item) => total + (item.unitPrice * item.quantity) + item.taxAmount, 0);
  }

  getItemTotal(item: CBox_GetInvoiceDataResponse["items"][number]): number {
    return (item.unitPrice * item.quantity) + item.taxAmount;
  }

  calculateBoxTotal(boxes: CBox_GetInvoiceDataResponse["metadata"]["boxes_counts_by_type"]): BoxTotals {
    return boxes?.reduce(
      (acc, r) => ({
        count: acc.count + r.total.count,
        discountCount: acc.discountCount + r.discount.count,
        amount: acc.amount + r.total.amount,
        discountAmount: acc.discountAmount + r.discount.amount,
        finalAmount: acc.finalAmount + r.final.amount,
      }),
      { count: 0, discountCount: 0, amount: 0, discountAmount: 0, finalAmount: 0 }
    ) ?? { count: 0, discountCount: 0, amount: 0, discountAmount: 0, finalAmount: 0 };
  }

  toggleDetailsFor(preInvoice: CBox_InvoiceListItem): void {
    this.detailsToggled.update(toggled => {
      toggled[preInvoice.identifier] = !toggled[preInvoice.identifier];
      return { ...toggled };
    });
  }

  getStatusSeverity(status: CBox_InvoiceEInvoiceStatus): "success" | "info" | "warning" | "danger" | "secondary" | "contrast" {
    switch (status) {
      case "QUEUED":
        return "secondary";
      case "UPLOADED":
        return "info";
      case "PROCESSED":
        return "info";
      case "SUCCESS":
        return "success";
      case "FAILURE":
        return "danger";
      default:
        return "secondary";
    }
  }

  showSuccessToast(message: string): void {
    this.toastService.showSuccessToast("Confirmare", message);
  }

  getSearchStructure(): Record<string, unknown> {
    let interval = this.filtersForm.value.interval;
    if (typeof interval === "string") interval = interval.split(",");
    return Object.entries({
      page: this.filtersForm.value.page,
      pageSize: this.filtersForm.value.pageSize,
      identifier: this.filtersForm.value.identifier,
      afterDate: interval[0] ? new Date(interval[0]).toISOString() : this.today,
      beforeDate: interval[1]
        ? new Date(interval[1]).toISOString()
        : this.oneMonthAgo,
    })
      .filter(([, v]) =>
        Array.isArray(v) ? v.length > 0 : v != null && (v as any).toString()
      )
      .reduce((a, [k, v]) => ((a[k] = v), a), {} as Record<string, unknown>);
  }

  private setAsDownloading(invoice: CBox_InvoiceListItem): void {
    this.downloading.update((downloadingItems) => {
      downloadingItems[invoice.identifier] = true;
      return downloadingItems;
    });
  }

  private setAsNotDownloading(invoice: CBox_InvoiceListItem): void {
    this.downloading.update((downloadingItems) => {
      delete downloadingItems[invoice.identifier];
      return { ...downloadingItems };
    });
  }
}
